import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { numberToWords } from '../../../utils/helper';

// ─── Design tokens (matches AddBookingFinance) ────────────────────────────────
const T = {
  bg:       '#f2f6f3',
  white:    '#ffffff',
  ink:      '#0c1810',
  ink2:     '#1e3328',
  subtle:   '#4d6b58',
  muted:    '#8ba898',
  border:   '#dae6df',
  borderL:  '#eaf2ec',
  green:    '#0a6b3e',
  greenMd:  '#0d8a50',
  greenLt:  '#e4f5ec',
  greenSf:  'rgba(10,107,62,0.07)',
  amber:    '#d97706',
  amberLt:  '#fef3c7',
  red:      '#dc2626',
  redLt:    '#fee2e2',
  redBdr:   '#fca5a5',
  blue:     '#1d4ed8',
  blueLt:   '#dbeafe',
  blueBdr:  '#93c5fd',
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, required, children, helper, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: T.subtle,
        letterSpacing: '0.2px', display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {label}
        {required && <span style={{ color: T.red }}>*</span>}
      </div>
      {children}
      {error  && <div style={{ fontSize: 11, color: T.red,  fontWeight: 600, marginTop: 1 }}>{error}</div>}
      {helper && !error && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{helper}</div>}
    </div>
  );
}

// ─── Styled input ─────────────────────────────────────────────────────────────
function Input({ name, value, onChange, type = 'text', placeholder, disabled, step, min, max, suffix }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        name={name} value={value} onChange={onChange}
        type={type} placeholder={placeholder}
        disabled={disabled} step={step} min={min} max={max}
        onFocus={() => setFocused(true)}
        onBlur={()  => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: suffix ? '9px 120px 9px 12px' : '9px 12px',
          fontSize: 13, color: disabled ? T.muted : T.ink,
          background: disabled ? '#eff4f1' : T.white,
          border: `1.5px solid ${focused ? T.green : T.border}`,
          borderRadius: 9, outline: 'none',
          cursor: disabled ? 'not-allowed' : 'text',
          transition: 'border-color 150ms, box-shadow 150ms',
          boxShadow: focused ? '0 0 0 3px rgba(10,107,62,0.1)' : 'none',
          fontFamily: 'inherit',
        }}
      />
      {suffix && (
        <span style={{
          position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, fontWeight: 700, color: T.green, pointerEvents: 'none',
          maxWidth: 114, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

// ─── Styled select ────────────────────────────────────────────────────────────
function Select({ name, value, onChange, options = [], placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <select
        name={name} value={value} onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={()  => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '9px 34px 9px 12px',
          fontSize: 13, color: value ? T.ink : T.muted,
          background: T.white,
          border: `1.5px solid ${focused ? T.green : T.border}`,
          borderRadius: 9, outline: 'none',
          appearance: 'none',
          cursor: 'pointer',
          transition: 'border-color 150ms, box-shadow 150ms',
          boxShadow: focused ? '0 0 0 3px rgba(10,107,62,0.1)' : 'none',
          fontFamily: 'inherit',
        }}
      >
        <option value="" disabled>{placeholder || 'Select…'}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {/* Chevron */}
      <svg style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function Section({ icon, title, children }) {
  return (
    <div style={{
      background: T.white, border: `1px solid ${T.border}`,
      borderRadius: 12, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: T.ink, letterSpacing: '-0.2px' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const AddBookingPaymentDialog = ({ open, onClose, booking, pendingAmount, paymentModes }) => {
  const { addBookingPayment } = useBookingDetails();

  const [formData, setFormData] = useState({
    amount: '',
    payment_method: '',
    payment_date: dayjs(),
    transaction_reference: '',
    notes: '',
    payment_details: {},
  });
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    if (formData.payment_method && formData.payment_method !== 'cash' && !formData.transaction_reference?.trim()) {
      setError('Transaction Reference is required for non-cash payments');
      setLoading(false);
      return;
    }

    try {
      const amount = Math.round(Number(formData.amount));
      await addBookingPayment(booking.id, {
        amount,
        payment_method:        formData.payment_method,
        payment_date:          formData.payment_date.format(),
        status:                'completed',
        transaction_reference: formData.transaction_reference || null,
        notes:                 formData.notes,
        payment_details:       formData.payment_details,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const amountNum = Number(formData.amount);
  const isOverPending = amountNum > pendingAmount;

  return (
    <>
      <style>{`
        @keyframes abpSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .abp-body { animation: abpSlideUp 220ms ease both; }
        .abp-body::-webkit-scrollbar { width: 5px; }
        .abp-body::-webkit-scrollbar-thumb { background: #ccddd5; border-radius: 99px; }
        .abp-body::-webkit-scrollbar-track { background: transparent; }
        .abp-textarea:focus { outline: none; border-color: ${T.green} !important; box-shadow: 0 0 0 3px rgba(10,107,62,0.1) !important; }
      `}</style>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '16px', overflow: 'hidden', m: 0,
            width: '100%', maxWidth: 500, maxHeight: '92vh',
            boxShadow: '0 24px 80px rgba(0,0,0,0.26)',
            border: `1.5px solid ${T.border}`,
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '92vh', maxHeight: '92vh' }}>

          {/* ── Header ───────────────────────────────────────────────── */}
          <div style={{
            background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
            padding: '18px 22px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 11,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              }}>💳</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>
                  Add Payment
                </div>
                {booking && (
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                    Recording payment for booking {booking.booking_id}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.14)', border: 'none',
                borderRadius: 8, width: 32, height: 32,
                fontSize: 20, color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >×</button>
          </div>

          {/* ── Info strip ───────────────────────────────────────────── */}
          <div style={{
            display: 'flex', gap: 0, flexShrink: 0,
            borderBottom: `1px solid ${T.border}`,
          }}>
            <div style={{
              flex: 1, padding: '10px 18px',
              borderRight: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 15 }}>🧾</span>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Booking ID</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{booking?.booking_id || '—'}</div>
              </div>
            </div>
            <div style={{
              flex: 1, padding: '10px 18px',
              background: pendingAmount > 0 ? T.redLt : T.greenLt,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 15 }}>⏳</span>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Pending Amount</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: pendingAmount > 0 ? T.red : T.green }}>
                  ₹{pendingAmount?.toLocaleString('en-IN') || '0'}
                </div>
              </div>
            </div>
          </div>

          {/* ── Scrollable body ──────────────────────────────────────── */}
          <div
            className="abp-body"
            style={{
              overflowY: 'auto', flex: 1, minHeight: 0,
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
              background: T.bg,
            }}
          >
            {/* Error */}
            {error && (
              <div style={{
                background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                borderRadius: 10, padding: '10px 14px',
                fontSize: 12.5, color: T.red, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ fontSize: 15 }}>⚠️</span> {error}
              </div>
            )}

            {/* 1 — Amount */}
            <Section icon="₹" title="Payment Amount">
              <Field
                label="Amount"
                required
                error={isOverPending ? `Cannot exceed pending amount of ₹${pendingAmount?.toLocaleString('en-IN')}` : undefined}
                helper={
                  formData.amount && !isOverPending
                    ? `${numberToWords(parseInt(formData.amount))} rupees only`
                    : undefined
                }
              >
                <Input
                  name="amount" value={formData.amount} onChange={handleChange}
                  type="number" placeholder="Enter amount" step={100} min={0} max={pendingAmount}
                  suffix={formData.amount && !isOverPending ? `₹${Number(formData.amount).toLocaleString('en-IN')}` : ''}
                />
              </Field>
            </Section>

            {/* 2 — Payment Details */}
            <Section icon="🏦" title="Payment Details">
              <Field label="Payment Method" required>
                <Select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  options={paymentModes || []}
                  placeholder="Select method"
                />
              </Field>
              <Field label="Payment Date" required>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.payment_date}
                    onChange={(d) => setFormData(p => ({ ...p, payment_date: d }))}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: '100%',
                          '& .MuiInputBase-root': { borderRadius: '9px', fontSize: '13px', background: T.white, fontFamily: 'inherit' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.green, borderWidth: '1.5px' },
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Field>
            </Section>

            {/* 3 — Transaction Reference */}
            <Section icon="🔗" title="Transaction Reference">
              <Field
                label="Transaction Reference"
                required={formData.payment_method && formData.payment_method !== 'cash'}
                helper={
                  formData.payment_method === 'cash'
                    ? 'Optional for cash payments'
                    : formData.payment_method
                    ? 'Required for non-cash payments'
                    : undefined
                }
              >
                <Input
                  name="transaction_reference"
                  value={formData.transaction_reference}
                  onChange={handleChange}
                  placeholder="e.g. UTR123456789"
                />
              </Field>
            </Section>

            {/* 4 — Notes */}
            <Section icon="📝" title="Notes">
              <Field label="Additional Notes">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any additional notes about this payment…"
                  rows={3}
                  className="abp-textarea"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', fontSize: 13, color: T.ink,
                    background: T.white, border: `1.5px solid ${T.border}`,
                    borderRadius: 9, resize: 'vertical', lineHeight: 1.65,
                    transition: 'border-color 150ms',
                    fontFamily: 'inherit',
                  }}
                />
              </Field>
            </Section>
          </div>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <div style={{
            padding: '13px 18px',
            borderTop: `1.5px solid ${T.border}`,
            background: '#f8fbf9', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
          }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                borderRadius: 9, padding: '8px 16px',
                fontSize: 12.5, fontWeight: 700, color: T.red,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1, transition: 'all 150ms',
                fontFamily: 'inherit',
              }}
            >✕ Cancel</button>

            <button
              onClick={handleSubmit}
              disabled={!formData.amount || !formData.payment_method || loading || isOverPending}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: (!formData.amount || !formData.payment_method || loading || isOverPending)
                  ? '#7ab898'
                  : 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
                border: 'none', borderRadius: 9, padding: '8px 20px',
                fontSize: 12.5, fontWeight: 700, color: '#fff',
                cursor: (!formData.amount || !formData.payment_method || loading || isOverPending) ? 'not-allowed' : 'pointer',
                boxShadow: (!formData.amount || !formData.payment_method || loading || isOverPending) ? 'none' : '0 2px 10px rgba(10,107,62,0.28)',
                transition: 'all 150ms',
                fontFamily: 'inherit',
              }}
            >
              {loading
                ? <>⏳ Saving…</>
                : <>💾 Add Payment</>
              }
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddBookingPaymentDialog;
